import { useTranslation } from "react-i18next";
import { CheckOutlined, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { Flex, theme } from "antd";
import { createStyles } from "antd-style";

import { Button } from "@av/components/atoms/Button";
import { AVA_ORB_IMAGE_PATH } from "../avaResumeAutofill.constants";

import type { AvaResumeAutofillStatus } from "../hooks/useAvaResumeAutofill";

const useStyles = createStyles(({ css, token }) => ({
  noticeCard: css`
    position: relative;
    width: 100%;
    min-height: 147px;
    margin-bottom: ${token.margin}px;
    padding: 10px;
    overflow: hidden;
    border-radius: 12px;
    background: linear-gradient(180.43deg, #e3eafb 9.5%, #ffffff 97.55%);
    box-shadow:
      0 1px 1px rgb(0 0 0 / 3%),
      0 1px 3px rgb(0 0 0 / 2%),
      0 2px 2px rgb(0 0 0 / 2%);

    &::before {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      height: 4px;
      background: linear-gradient(90deg, #8752df 0%, #5582ec 52%, #52c8c4 100%);
      content: "";
    }
  `,
  parsingNoticeCard: css`
    min-height: 177px;
  `,
  errorNoticeCard: css`
    min-height: 177px;
    background: linear-gradient(183.69deg, #fffbe6 9.5%, #ffffff 97.55%);
    box-shadow:
      0 1px 2px rgb(0 0 0 / 3%),
      0 1px 6px -1px rgb(0 0 0 / 2%),
      0 2px 4px rgb(0 0 0 / 2%);

    &::before {
      background: linear-gradient(90deg, #faad14 0%, #8752df 52%, #52c8c4 100%);
    }
  `,
  compactNoticeCard: css`
    min-height: 124px;
  `,
  header: css`
    height: 44px;
    padding: 0 ${token.paddingSM}px;

    @media (max-width: ${token.screenSMMax}px) {
      height: auto;
      min-height: 44px;
    }
  `,
  stackedHeader: css`
    height: 54px;
  `,
  orb: css`
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    object-fit: cover;
  `,
  titleStack: css`
    min-width: 0;
  `,
  title: css`
    min-width: 0;
    overflow: hidden;
    color: ${token.colorText};
    font-size: ${token.fontSize}px;
    font-weight: ${token.fontWeightStrong};
    line-height: 22px;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: ${token.screenSMMax}px) {
      white-space: normal;
    }
  `,
  subtitle: css`
    overflow: hidden;
    color: ${token.colorTextSecondary};
    font-size: ${token.fontSize}px;
    font-weight: 400;
    line-height: 22px;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  body: css`
    min-height: 82px;
    padding: ${token.paddingXS}px ${token.paddingSM}px;
    border-top: 1px solid ${token.colorWhite};
  `,
  parsingBody: css`
    min-height: 113px;
  `,
  errorBody: css`
    min-height: 103px;
  `,
  compactBody: css`
    min-height: 40px;
    padding-top: 0;
    border-top: 0;
  `,
  description: css`
    width: 100%;
    margin: 0;
    color: ${token.colorText};
    font-size: ${token.fontSize}px;
    line-height: 22px;
  `,
  actions: css`
    width: 100%;
    min-width: 0;
  `,
  action: css`
    width: min(100%, 329px);
    min-width: 0;
    max-width: 100%;

    @media (max-width: ${token.screenSMMax}px) {
      height: auto;
      min-height: ${token.controlHeight}px;
      white-space: normal;
    }
  `,
  noResumeAction: css`
    width: fit-content;
    min-width: min(100%, 329px);
    max-width: 100%;
  `,
  reuploadAction: css`
    width: auto;
    max-width: 100%;
  `,
  errorAction: css`
    width: auto;
  `,
  secondaryAction: css`
    padding-right: ${token.paddingXS}px;
    padding-left: ${token.paddingXS}px;
  `,
  undoAction: css`
    border-color: #8752df;
    color: #7b5ea7;

    &:hover,
    &:focus {
      border-color: #8752df !important;
      color: #7b5ea7 !important;
    }
  `,
  loadingRow: css`
    color: #7b5ea7;
    font-size: ${token.fontSize}px;
    font-weight: ${token.fontWeightStrong};
    line-height: 17px;
  `,
  loadingIcon: css`
    flex: 0 0 16px;
    color: #7b5ea7;
    font-size: 16px;
  `,
}));

type AvaResumeNoticeCardProps = {
  status: AvaResumeAutofillStatus;
  description?: string;
  autofilledFieldCount: number;
  onPrimaryAction: () => void;
  onUndo: () => void;
  onClear: () => void;
};

type NoticeCopy = {
  titleKey: string;
  descriptionKey: string;
  actionKey?: string;
};

const NOTICE_COPY: Record<AvaResumeAutofillStatus, NoticeCopy> = {
  noResume: {
    titleKey: "recruitment.jobApplication.avaResumeNotice_noResumeTitle",
    descriptionKey: "recruitment.jobApplication.avaResumeNotice_noResumeDescription",
    actionKey: "recruitment.jobApplication.avaResumeNotice_noResumeAction",
  },
  ready: {
    titleKey: "recruitment.jobApplication.avaResumeNotice_readyTitle",
    descriptionKey: "recruitment.jobApplication.avaResumeNotice_readyDescription",
    actionKey: "recruitment.jobApplication.avaResumeNotice_readyAction",
  },
  parsing: {
    titleKey: "recruitment.jobApplication.avaResumeNotice_parsingTitle",
    descriptionKey: "recruitment.jobApplication.avaResumeNotice_parsingDescription",
  },
  success: {
    titleKey: "recruitment.jobApplication.avaResumeNotice_successTitle",
    descriptionKey: "recruitment.jobApplication.avaResumeNotice_successDescription",
  },
  partial: {
    titleKey: "recruitment.jobApplication.avaResumeNotice_partialTitle",
    descriptionKey: "recruitment.jobApplication.avaResumeNotice_partialDescription",
  },
  error: {
    titleKey: "recruitment.jobApplication.avaResumeNotice_errorTitle",
    descriptionKey: "recruitment.jobApplication.avaResumeNotice_errorDescription",
    actionKey: "recruitment.jobApplication.avaResumeNotice_errorAction",
  },
  reupload: {
    titleKey: "recruitment.jobApplication.avaResumeNotice_reuploadTitle",
    descriptionKey: "recruitment.jobApplication.avaResumeNotice_reuploadDescription",
    actionKey: "recruitment.jobApplication.avaResumeNotice_refillAction",
  },
  undo: {
    titleKey: "recruitment.jobApplication.avaResumeNotice_undoTitle",
    descriptionKey: "recruitment.jobApplication.avaResumeNotice_undoDescription",
    actionKey: "recruitment.jobApplication.avaResumeNotice_refillAction",
  },
};

export function AvaResumeNoticeCard({
  status,
  description,
  autofilledFieldCount,
  onPrimaryAction,
  onUndo,
  onClear,
}: AvaResumeNoticeCardProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { styles } = useStyles();
  const copy = NOTICE_COPY[status];
  const hasAutofilledFields = autofilledFieldCount > 0;
  const isParsing = status === "parsing";
  const isFilled = status === "success" || status === "partial";
  const isReupload = status === "reupload";
  const isError = status === "error";
  const hasStackedHeader = isFilled || isReupload;
  const isNoResume = status === "noResume";
  const descriptionText = description || t(copy.descriptionKey);
  const undoActionLabel = t("recruitment.jobApplication.avaResumeNotice_undoAction");
  const showActions =
    !isParsing && (copy.actionKey !== undefined || hasAutofilledFields || isFilled);

  return (
    <section
      className={`${styles.noticeCard} ${isParsing ? styles.parsingNoticeCard : ""} ${
        isError ? styles.errorNoticeCard : ""
      } ${hasStackedHeader ? styles.compactNoticeCard : ""}`}
      data-testid="candidate-application-ava-notice"
      data-ava-state={status}
      aria-live="polite"
    >
      <Flex
        align="center"
        gap={token.marginSM}
        className={`${styles.header} ${hasStackedHeader ? styles.stackedHeader : ""}`}
      >
        <img
          className={styles.orb}
          src={AVA_ORB_IMAGE_PATH}
          alt=""
          aria-hidden="true"
          data-testid="candidate-application-ava-notice-orb"
        />
        {hasStackedHeader ? (
          <Flex vertical justify="center" gap={token.marginXXS} className={styles.titleStack}>
            <div className={styles.title}>{t(copy.titleKey)}</div>
            <div className={styles.subtitle}>{t(copy.descriptionKey)}</div>
          </Flex>
        ) : (
          <div className={styles.title}>{t(copy.titleKey)}</div>
        )}
      </Flex>

      <Flex
        vertical
        align="flex-start"
        justify={isParsing || isError || hasStackedHeader ? "flex-start" : "center"}
        gap={isParsing ? token.marginLG : isError ? token.marginXXS : token.marginSM}
        className={`${styles.body} ${isParsing ? styles.parsingBody : ""} ${
          isError ? styles.errorBody : ""
        } ${hasStackedHeader ? styles.compactBody : ""}`}
      >
        {!hasStackedHeader && <p className={styles.description}>{descriptionText}</p>}

        {isParsing && (
          <Flex align="center" gap={token.marginXS} className={styles.loadingRow}>
            <LoadingOutlined spin className={styles.loadingIcon} aria-hidden="true" />
            <span>{t("recruitment.jobApplication.avaResumeNotice_parsingLoading")}</span>
          </Flex>
        )}

        {showActions && (
          <Flex
            wrap
            gap={token.marginSM}
            className={styles.actions}
            data-testid="candidate-application-ava-notice-actions"
          >
            {isFilled && (
              <Button
                className={styles.undoAction}
                icon={<CheckOutlined />}
                aria-label={undoActionLabel}
                size="middle"
                type="default"
                testIdPrefix="candidate-application-ava-notice-undo"
                onClick={onUndo}
              >
                {undoActionLabel}
              </Button>
            )}

            {copy.actionKey && (
              <Button
                className={`${styles.action} ${isNoResume ? styles.noResumeAction : ""} ${
                  isReupload ? styles.reuploadAction : ""
                } ${isError ? styles.errorAction : ""}`}
                disabled={isNoResume}
                icon={isError ? <ReloadOutlined aria-hidden="true" /> : undefined}
                size="middle"
                type={isError ? "link" : isNoResume ? "default" : "primary"}
                testIdPrefix="candidate-application-ava-notice-primary-action"
                onClick={onPrimaryAction}
              >
                {t(copy.actionKey)}
              </Button>
            )}

            {hasAutofilledFields && !isFilled && !isReupload && !isError && (
              <>
                <Button
                  className={styles.secondaryAction}
                  size="middle"
                  type="link"
                  testIdPrefix="candidate-application-ava-notice-undo"
                  onClick={onUndo}
                >
                  {undoActionLabel}
                </Button>
                <Button
                  className={styles.secondaryAction}
                  size="middle"
                  type="link"
                  testIdPrefix="candidate-application-ava-notice-clear"
                  onClick={onClear}
                >
                  {t("recruitment.jobApplication.avaResumeNotice_clearAction")}
                </Button>
              </>
            )}
          </Flex>
        )}
      </Flex>
    </section>
  );
}
